if (!customElements.get('product-information')) {
  customElements.define('product-information', class ProductInformation extends HTMLElement {
    staticBuyTools = true;
    quantityInput = undefined;
    onVariantChangeUnsubscriber = undefined;
    cartUpdateUnsubscriber = undefined;
    abortController = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];
    silverBulletOverviewLayoutFrame = undefined;
    silverBulletOverviewLayoutObserver = undefined;
    silverBulletOverviewLayoutMediaQuery = undefined;
    silverBulletOverviewLayoutMediaQueryHandler = undefined;
    silverBulletOverviewDesiredGap = undefined;
    silverBulletOverviewLift = 0;

    constructor() {
      super();
      this.buyToolsTrigger = this.querySelector('#product__buy-tools-trigger');
      this.productInfoContainer = this.querySelector('.product__info-container');
      this.onScrollToolsHandler = this.onScrollBuyTools.bind(this);
      this.quantityInput = this.querySelector(`#QuantityInput-${this.sectionId}`);

      this.initBreakpoints();
    }

    initBreakpoints() {
      const self = this;

      enquire.register('screen and (max-width: 1000px)', {
        match: () => {
          self.bpSmall = true;
          this.productInfoContainer.classList.remove('product__info-container--modal--out');
          this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
        },
        unmatch: () => {
          self.bpSmall = false;
          this.productInfoContainer.classList.remove('product__info-container--modal--out');
          this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
        }
      });
    }

    connectedCallback() {
      this.initializeProductSwapUtility();
      this.initializeSilverBulletOverviewLayout();

      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );
    }

    addPreProcessCallback(callback) {
      this.preProcessHtmlCallbacks.push(callback);
    }

    disconnectedCallback() {
      this.onVariantChangeUnsubscriber();
      this.silverBulletOverviewLayoutObserver?.disconnect();
      if (this.silverBulletOverviewLayoutFrame) {
        cancelAnimationFrame(this.silverBulletOverviewLayoutFrame);
      }
      if (this.silverBulletOverviewLayoutMediaQuery && this.silverBulletOverviewLayoutMediaQueryHandler) {
        if (typeof this.silverBulletOverviewLayoutMediaQuery.removeEventListener === 'function') {
          this.silverBulletOverviewLayoutMediaQuery.removeEventListener(
            'change',
            this.silverBulletOverviewLayoutMediaQueryHandler
          );
        } else {
          this.silverBulletOverviewLayoutMediaQuery.removeListener(
            this.silverBulletOverviewLayoutMediaQueryHandler
          );
        }
      }
      // this.cartUpdateUnsubscriber?.();
    }

    initializeSilverBulletOverviewLayout() {
      // Preserve the H1-first source order while closing the independent desktop media-column gap.
      const productPage = this.querySelector('.sb-product-page');
      const mediaWrapper = productPage?.querySelector('.product__media-wrapper');
      const overview = this.querySelector('.sb-product-overview--responsive');

      if (!productPage || !mediaWrapper || !overview || typeof ResizeObserver === 'undefined') return;

      this.silverBulletOverviewLayoutMediaQuery = window.matchMedia('(min-width: 1001px)');

      const scheduleLayout = () => {
        // ResizeObserver can notify for the same layout pass more than once. Keep one
        // read/write frame in flight instead of cancelling and forcing another measure.
        if (this.silverBulletOverviewLayoutFrame !== undefined) return;

        this.silverBulletOverviewLayoutFrame = requestAnimationFrame(() => {
          this.silverBulletOverviewLayoutFrame = undefined;

          if (!this.silverBulletOverviewLayoutMediaQuery.matches) {
            this.silverBulletOverviewDesiredGap = undefined;
            if (this.silverBulletOverviewLift !== 0 || overview.style.getPropertyValue('--sb-product-overview-lift')) {
              this.silverBulletOverviewLift = 0;
              overview.style.removeProperty('--sb-product-overview-lift');
            }
            return;
          }

          // Batch all geometry reads before the custom-property write. The margin is
          // stable within this desktop layout, so compute it only on entry/re-entry.
          const overviewRect = overview.getBoundingClientRect();
          const mediaRect = mediaWrapper.getBoundingClientRect();
          if (this.silverBulletOverviewDesiredGap === undefined) {
            this.silverBulletOverviewDesiredGap = Math.max(
              24,
              parseFloat(window.getComputedStyle(overview).marginTop) || 36
            );
          }

          const currentOverviewTop = overviewRect.top;
          const naturalOverviewTop = currentOverviewTop + this.silverBulletOverviewLift;
          const nextLift = Math.max(
            0,
            Math.round(
              (naturalOverviewTop - mediaRect.bottom - this.silverBulletOverviewDesiredGap) * 100
            ) / 100
          );

          if (Math.abs(nextLift - this.silverBulletOverviewLift) < 0.5) return;

          this.silverBulletOverviewLift = nextLift;
          overview.style.setProperty('--sb-product-overview-lift', `${nextLift}px`);
        });
      };

      this.silverBulletOverviewLayoutObserver = new ResizeObserver(scheduleLayout);
      this.silverBulletOverviewLayoutObserver.observe(productPage);
      this.silverBulletOverviewLayoutObserver.observe(mediaWrapper);

      this.silverBulletOverviewLayoutMediaQueryHandler = () => {
        this.silverBulletOverviewDesiredGap = undefined;
        scheduleLayout();
      };
      if (typeof this.silverBulletOverviewLayoutMediaQuery.addEventListener === 'function') {
        this.silverBulletOverviewLayoutMediaQuery.addEventListener(
          'change',
          this.silverBulletOverviewLayoutMediaQueryHandler
        );
      } else {
        this.silverBulletOverviewLayoutMediaQuery.addListener(
          this.silverBulletOverviewLayoutMediaQueryHandler
        );
      }
    }

    initializeProductSwapUtility() {
      this.preProcessHtmlCallbacks.push((html) => {
        html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
      });
      this.postProcessHtmlCallbacks.push((newNode) => {
        window?.Shopify?.PaymentButton?.init();
        window?.ProductModel?.loadShopifyXR();
      });
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      this.resetProductFormState();

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
          : this.handleUpdateProductInfo(productUrl),
      });
    }

    resetProductFormState() {
      const productForm = this.productForm;
      this.toggleSubmitButton(true);
      productForm?.handleErrorMessage();
    }

    handleSwapProduct(productUrl, updateFullPage) {
      return (html) => {
        this.productModal?.remove();

        const selector = updateFullPage ? "product-information[id^='MainProduct']" : 'product-information';
        const variant = this.getSelectedVariant(html.querySelector(selector));
        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            html.querySelector('product-information'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }
      };
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    getSelectedVariant(productInfoNode) {
      const selector = 'variant-selects [data-selected-variant]';

      const selectedVariant = productInfoNode.querySelector(`${selector}`)?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    updateOptionValues(html) {
      const sectionId = `${this.sectionId}${this.blockId ? `${this.blockId}`: ''}`;
      const variantSelects = html.getElementById(`VariantSelect-${sectionId}`);

      if (variantSelects) {
        const isOnProductModal =
          this.closest('complementary-products-card') ||
          this.closest('quick-add-modal') ||
          this.closest('.shop-look__item-wrap');
        if (isOnProductModal) {
          const updatedOptions = variantSelects.querySelectorAll('fieldset');

          this.variantSelectors.querySelectorAll('input[data-disabled="true"]').forEach((i) => {
            i.removeAttribute('data-disabled');
          });

          updatedOptions.forEach((option) => {
            option.querySelectorAll('input[data-disabled="true"]').forEach((i) => {
              const inputId = i.dataset.optionValueId;
              const currentNode = this.variantSelectors.querySelector(`input[data-option-value-id="${inputId}"]`);
              if (currentNode?.tagName === 'INPUT' && currentNode?.type === 'radio') {
                currentNode.setAttribute('data-disabled', 'true');
              }
            });
          });
        } else {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }
    }

    handleUpdateProductInfo(productUrl) {
      return (html) => {
        const variant = this.getSelectedVariant(html);
        const sectionId = `${this.sectionId}${this.blockId ? `${this.blockId}`: ''}`;

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant, variant?.featured_media?.id);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('visibility-hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('sku', ({ classList }) => classList.contains('visibility-hidden'));
        updateSourceFromDestination('StockNotification', ({ innerText }) => innerText === '');

        this.updateLimitQuantity(this.sectionId, html);

        this.toggleSubmitButton(
          html.getElementById(`ProductSubmitButton-${sectionId}`)?.hasAttribute('disabled') ?? true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });
      };
    }

    updateLimitQuantity(sectionId, html) {
      if (!this.quantityInput) return;
      const quantityFormUpdated = html.getElementById(`QuantityInput-${sectionId}`);

      this.quantityInput.updateLimitQuantity(quantityFormUpdated);
    }

    updateVariantInputs(variantId) {
      const sectionId = `${this.dataset.section}`;

      this.querySelectorAll(
        `#product-form-${sectionId}, #product-form-installment-${sectionId}`
      ).forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateURL(url, variantId) {
      const destination = new URL(url, window.location.origin);
      const currentParameters = new URLSearchParams(window.location.search);
      ['preview_theme_id', 'year', 'make', 'model', 'bed'].forEach((key) => {
        const value = currentParameters.get(key);
        if (value && !destination.searchParams.has(key)) destination.searchParams.set(key, value);
      });
      if (variantId) destination.searchParams.set('variant', variantId);
      else destination.searchParams.delete('variant');

      this.querySelector('share-button')?.updateUrl(destination.toString());

      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState(
        {},
        '',
        `${destination.pathname}${destination.search}${destination.hash}`
      );
    }

    toggleSubmitButton(disable = true, text) {
      const productForm = this.productForm?.querySelector('form');
      if (!productForm) return;

      const addButtons = Array.from(productForm.querySelectorAll('[data-add-to-cart]'));
      if (addButtons.length === 0) return;
      const availableLabel = addButtons[0].dataset.availableLabel || window.variantStrings.addToCart;

      const variantClassSoldOut = 'product-form--variant-sold-out';
      const formParentElement = productForm.closest('.product-form__buy-buttons');
      const productMetaPrice = formParentElement ? formParentElement.querySelector('[data-product-meta-price]') : null;

      if (disable) {
        addButtons.forEach((addButton) => {
          addButton.setAttribute('aria-disabled', true);
          addButton.setAttribute('disabled', true);
          addButton.setAttribute('aria-label', text);
          const addButtonText = addButton.querySelector('[data-add-to-cart-text]');
          if (text && addButtonText) addButtonText.textContent = text;
        });
        if (text) {
          productForm.classList.add(variantClassSoldOut);
          if (productMetaPrice) productMetaPrice.classList.add('meta-price--hidden');
        }
      } else {
        addButtons.forEach((addButton) => {
          const buttonAvailableLabel = addButton.dataset.availableLabel || availableLabel;
          addButton.removeAttribute('aria-disabled');
          addButton.removeAttribute('disabled');
          addButton.setAttribute('aria-label', buttonAvailableLabel);
          const addButtonText = addButton.querySelector('[data-add-to-cart-text]');
          if (addButtonText) addButtonText.textContent = buttonAvailableLabel;
        });
        productForm.classList.remove(variantClassSoldOut);
        if (productMetaPrice) productMetaPrice.classList.remove('meta-price--hidden');
      }
    }

    setUnavailable() {
      this.toggleSubmitButton(true, window.variantStrings.unavailable);

      const selectors = ['price', 'sku', 'StockNotification']
        .map((id) => `#${id}-${this.dataset.section}`)
        .join(', ');

      document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('visibility-hidden'));
    }

    updateMedia(variant, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;

      const sectionId = `${this.dataset.section}`;
      const mediaGallery = this.querySelector(`#MediaGallery-${sectionId}`);
      if (!mediaGallery) return;
      mediaGallery.setActiveMedia(`${sectionId}-${variantFeaturedMediaId}`, variant);
    }

    buyToolsCalculations() {
      this.staticBuyTools = false;
      this.productPageSection = this.parentElement;
      this.productInfoWrapper = this.productPageSection.querySelector('.product__info-wrapper');
      this.productInfoContainerHeight = this.productInfoContainer.offsetHeight;
      this.negativeHeight = this.productInfoContainerHeight * -1;
      this.productContentSection = document.querySelector('.product-content-section');

      const productContentMinHeight = this.productInfoContainerHeight - this.productPageSection.offsetHeight + 54;
      this.productContentSection.style.minHeight = `${productContentMinHeight}px`;

      this.resizeProductInfoObserver = new ResizeObserver((entries) => {
        if (this.bpSmall) return;
        if (!this.productInfoContainer.classList.contains('product__info-container--modal')) {
          const productInfoContainerHeight = this.productInfoContainer.offsetHeight;
          if (productInfoContainerHeight != this.productInfoContainerHeight) {
            this.productInfoContainerHeight = productInfoContainerHeight;
            const productContentMinHeight = productInfoContainerHeight - this.productPageSection.offsetHeight + 54;
            this.productContentSection.style.minHeight = `${productContentMinHeight}px`;

            this.negativeHeight = productInfoContainerHeight * -1;
          }
        }
      });

      this.resizeProductInfoObserver.observe(this.productInfoContainer);
    }

    onScrollBuyTools() {
      if (this.bpSmall || !this.buyToolsTrigger ) return;

      const triggerTop = this.buyToolsTrigger.getBoundingClientRect().top;
      const buyToolsModalActive = this.productInfoContainer.classList.contains('product__info-container--modal', 'background-modal');
      const bytToolsModalOut = this.productInfoContainer.classList.contains('product__info-container--modal--out');

      if (triggerTop < this.negativeHeight && !buyToolsModalActive) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add('product__info-container--modal', 'background-modal');
        });
      } else if (triggerTop > this.negativeHeight && buyToolsModalActive && !bytToolsModalOut) {
        requestAnimationFrame(() => {
          this.productInfoContainer.classList.add('product__info-container--modal--out');

          this.productInfoContainer.addEventListener('animationend', () => {
            this.productInfoContainer.classList.remove('product__info-container--modal--out');
            this.productInfoContainer.classList.remove('product__info-container--modal', 'background-modal');
          }, { once: true });
        });
      }
    }

    get productForm() {
      return this.querySelector(`product-form`);
    }

    get productModal() {
      return document.querySelector(`#ProductModal-${this.sectionId}`);
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get variantSelectors() {
      return this.querySelector('variant-selects');
    }

    get relatedProducts() {
      const relatedProductsSectionId = SectionId.getIdForSection(
        SectionId.parseId(this.sectionId),
        'related-products'
      );
      return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.section;
    }

    get blockId() {
      return this.dataset.block;
    }

    get productId() {
      return this.dataset.productId;
    }
  });
}
